import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { loadImageFile, type ImageLoader, type LoadedImage } from './load-image';

/**
 * PP-1 — headless square-crop state (PP spec §5.1).
 *
 * No JSX, no tokens, no store, no HTTP client: a plane-neutral hook that
 * turns pointer / wheel / pinch / keyboard / slider input into ONE transform
 * (`scale`, `offset`) over a square viewport, and exports the visible square
 * as a JPEG `File`. Shells on either identity plane render it with their own
 * components and styling. The server normalises whatever it receives, so the
 * export is a suggestion, not a contract.
 *
 * ── Geometry ───────────────────────────────────────────────────────────────
 * Coordinates are viewport pixels. The image is drawn at its natural size,
 * scaled by `scale`, with its top-left at `offset`. "Cover" is the invariant:
 * `scale ≥ minScale` (image at least fills the square) and `offset` is clamped
 * so no viewport pixel is ever outside the image. The exported crop is the
 * source rectangle currently under the viewport.
 */

export interface Point {
  x: number;
  y: number;
}
export interface Size {
  w: number;
  h: number;
}

export const DEFAULT_VIEWPORT_PX = 320;
export const DEFAULT_EXPORT_PX = 512;
export const DEFAULT_EXPORT_QUALITY = 0.9;
/** `maxScale = minScale × MAX_ZOOM_FACTOR` — 4× past "cover" is plenty for a face. */
export const MAX_ZOOM_FACTOR = 4;
export const KEY_PAN_STEP = 10;
export const KEY_PAN_STEP_FAST = 50;
export const KEY_ZOOM_FACTOR = 1.1;
export const WHEEL_ZOOM_SENSITIVITY = 0.0015;

export type CropStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface CropState {
  status: CropStatus;
  error: string | null;
  loaded: LoadedImage | null;
  natural: Size;
  scale: number;
  minScale: number;
  maxScale: number;
  offset: Point;
}

const IDLE: CropState = {
  status: 'idle',
  error: null,
  loaded: null,
  natural: { w: 0, h: 0 },
  scale: 1,
  minScale: 1,
  maxScale: MAX_ZOOM_FACTOR,
  offset: { x: 0, y: 0 },
};

// ── Pure geometry (exported for tests and for the customer shell) ───────────

export function coverScale(natural: Size, viewport: number): number {
  if (natural.w <= 0 || natural.h <= 0) return 1;
  return Math.max(viewport / natural.w, viewport / natural.h);
}

export function clampOffset(offset: Point, natural: Size, scale: number, viewport: number): Point {
  const minX = viewport - natural.w * scale;
  const minY = viewport - natural.h * scale;
  return {
    x: Math.min(0, Math.max(minX, offset.x)),
    y: Math.min(0, Math.max(minY, offset.y)),
  };
}

export function centredOffset(natural: Size, scale: number, viewport: number): Point {
  return { x: (viewport - natural.w * scale) / 2, y: (viewport - natural.h * scale) / 2 };
}

/** Rescales about `point` (viewport px) so the image pixel under it stays put. */
export function zoomAbout(
  state: Pick<CropState, 'scale' | 'offset' | 'natural' | 'minScale' | 'maxScale'>,
  nextScale: number,
  point: Point,
  viewport: number,
): Pick<CropState, 'scale' | 'offset'> {
  const scale = Math.min(state.maxScale, Math.max(state.minScale, nextScale));
  const ratio = scale / state.scale;
  const offset = {
    x: point.x - (point.x - state.offset.x) * ratio,
    y: point.y - (point.y - state.offset.y) * ratio,
  };
  return { scale, offset: clampOffset(offset, state.natural, scale, viewport) };
}

/** The source rectangle (image px) currently visible in the viewport. */
export function sourceRect(
  state: Pick<CropState, 'scale' | 'offset'>,
  viewport: number,
): { sx: number; sy: number; sw: number; sh: number } {
  // `+ 0` folds a negative zero (from `-offset / scale` when offset is 0) into +0.
  return {
    sx: -state.offset.x / state.scale + 0,
    sy: -state.offset.y / state.scale + 0,
    sw: viewport / state.scale,
    sh: viewport / state.scale,
  };
}

function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}
function midpoint(a: Point, b: Point): Point {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

// ── Hook ────────────────────────────────────────────────────────────────────

export interface UseImageCropOptions {
  /** Fallback viewport size when the element cannot be measured (tests, SSR). */
  viewport?: number;
  loadImage?: ImageLoader;
  ariaLabel?: string;
}

export interface ViewportProps {
  ref: (el: HTMLElement | null) => void;
  tabIndex: 0;
  role: 'group';
  'aria-label': string;
  'aria-busy': boolean;
  onPointerDown: (e: ReactPointerEvent<HTMLElement>) => void;
  onPointerMove: (e: ReactPointerEvent<HTMLElement>) => void;
  onPointerUp: (e: ReactPointerEvent<HTMLElement>) => void;
  onPointerCancel: (e: ReactPointerEvent<HTMLElement>) => void;
  onKeyDown: (e: ReactKeyboardEvent<HTMLElement>) => void;
  style: CSSProperties;
}

export const DEFAULT_ARIA_LABEL =
  'Photo crop area. Drag to move the photo. Use the arrow keys to move, plus and minus to zoom, Home to reset.';

export function useImageCrop(file: File | null, options: UseImageCropOptions = {}) {
  // The loader lives in a ref so an inline `loadImage` option (a new function
  // every render) cannot re-trigger the load effect and loop.
  const loaderRef = useRef<ImageLoader>(options.loadImage ?? loadImageFile);
  loaderRef.current = options.loadImage ?? loadImageFile;
  const fallbackViewport = options.viewport ?? DEFAULT_VIEWPORT_PX;

  const [viewportEl, setViewportEl] = useState<HTMLElement | null>(null);
  const [viewport, setViewport] = useState<number>(fallbackViewport);
  const [crop, setCrop] = useState<CropState>(IDLE);

  // Latest state for imperative readers (export, wheel) without stale closures.
  const cropRef = useRef(crop);
  cropRef.current = crop;
  const viewportRef = useRef(viewport);
  viewportRef.current = viewport;

  const pointers = useRef<Map<number, Point>>(new Map());
  const pinch = useRef<{ dist: number; mid: Point } | null>(null);

  // ── Load ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!file) {
      setCrop(IDLE);
      return;
    }
    let cancelled = false;
    let loaded: LoadedImage | null = null;
    setCrop({ ...IDLE, status: 'loading' });
    loaderRef
      .current(file)
      .then((img) => {
        if (cancelled) {
          img.release();
          return;
        }
        loaded = img;
        const natural = { w: img.width, h: img.height };
        const v = viewportRef.current;
        const minScale = coverScale(natural, v);
        setCrop({
          status: 'ready',
          error: null,
          loaded: img,
          natural,
          scale: minScale,
          minScale,
          maxScale: minScale * MAX_ZOOM_FACTOR,
          offset: centredOffset(natural, minScale, v),
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setCrop({
          ...IDLE,
          status: 'error',
          error: err instanceof Error ? err.message : 'IMAGE_UNREADABLE',
        });
      });
    return () => {
      cancelled = true;
      loaded?.release();
    };
  }, [file]);

  // ── Measure the viewport; re-derive the transform when its size changes ──
  useEffect(() => {
    if (!viewportEl) return;
    const apply = () => {
      const width = Math.round(viewportEl.getBoundingClientRect().width);
      if (width > 0) setViewport(width);
    };
    apply();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(apply);
    ro.observe(viewportEl);
    return () => ro.disconnect();
  }, [viewportEl]);

  const previousViewport = useRef(viewport);
  useEffect(() => {
    const prev = previousViewport.current;
    previousViewport.current = viewport;
    if (prev === viewport || prev <= 0) return;
    const k = viewport / prev;
    setCrop((c) => {
      if (c.status !== 'ready') return c;
      const minScale = coverScale(c.natural, viewport);
      const scale = Math.max(minScale, c.scale * k);
      return {
        ...c,
        minScale,
        maxScale: minScale * MAX_ZOOM_FACTOR,
        scale,
        offset: clampOffset({ x: c.offset.x * k, y: c.offset.y * k }, c.natural, scale, viewport),
      };
    });
  }, [viewport]);

  // ── Transform helpers ─────────────────────────────────────────────────────
  const panBy = useCallback((dx: number, dy: number) => {
    setCrop((c) => {
      if (c.status !== 'ready') return c;
      return {
        ...c,
        offset: clampOffset(
          { x: c.offset.x + dx, y: c.offset.y + dy },
          c.natural,
          c.scale,
          viewportRef.current,
        ),
      };
    });
  }, []);

  const zoomTo = useCallback((nextScale: number, point?: Point) => {
    setCrop((c) => {
      if (c.status !== 'ready') return c;
      const v = viewportRef.current;
      const p = point ?? { x: v / 2, y: v / 2 };
      return { ...c, ...zoomAbout(c, nextScale, p, v) };
    });
  }, []);

  const zoomBy = useCallback(
    (factor: number, point?: Point) => {
      zoomTo(cropRef.current.scale * factor, point);
    },
    [zoomTo],
  );

  const setScale = useCallback((value: number) => zoomTo(value), [zoomTo]);

  const reset = useCallback(() => {
    setCrop((c) => {
      if (c.status !== 'ready') return c;
      const v = viewportRef.current;
      return { ...c, scale: c.minScale, offset: centredOffset(c.natural, c.minScale, v) };
    });
  }, []);

  const localPoint = (el: HTMLElement, clientX: number, clientY: number): Point => {
    const rect = el.getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  // ── Pointer (drag / pinch) ────────────────────────────────────────────────
  const onPointerDown = useCallback((e: ReactPointerEvent<HTMLElement>) => {
    if (cropRef.current.status !== 'ready') return;
    e.preventDefault();
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } catch {
      // jsdom and some WebViews have no pointer capture; drag still works while inside.
    }
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()] as [Point, Point];
      pinch.current = { dist: distance(a, b), mid: midpoint(a, b) };
    }
  }, []);

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      const prev = pointers.current.get(e.pointerId);
      if (!prev) return;
      const cur = { x: e.clientX, y: e.clientY };
      pointers.current.set(e.pointerId, cur);

      if (pointers.current.size >= 2 && pinch.current) {
        const [a, b] = [...pointers.current.values()] as [Point, Point];
        const dist = distance(a, b);
        const mid = midpoint(a, b);
        const ratio = pinch.current.dist > 0 ? dist / pinch.current.dist : 1;
        const dx = mid.x - pinch.current.mid.x;
        const dy = mid.y - pinch.current.mid.y;
        pinch.current = { dist, mid };
        const el = e.currentTarget;
        setCrop((c) => {
          if (c.status !== 'ready') return c;
          const v = viewportRef.current;
          const panned = clampOffset(
            { x: c.offset.x + dx, y: c.offset.y + dy },
            c.natural,
            c.scale,
            v,
          );
          return {
            ...c,
            ...zoomAbout(
              { ...c, offset: panned },
              c.scale * ratio,
              localPoint(el, mid.x, mid.y),
              v,
            ),
          };
        });
        return;
      }
      panBy(cur.x - prev.x, cur.y - prev.y);
    },
    [panBy],
  );

  const endPointer = useCallback((e: ReactPointerEvent<HTMLElement>) => {
    pointers.current.delete(e.pointerId);
    try {
      if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
    } catch {
      // see onPointerDown
    }
    if (pointers.current.size < 2) pinch.current = null;
  }, []);

  // ── Wheel (native, non-passive so preventDefault stops page scroll) ──────
  useEffect(() => {
    if (!viewportEl) return;
    const handler = (e: WheelEvent) => {
      if (cropRef.current.status !== 'ready') return;
      e.preventDefault();
      const factor = Math.exp(-e.deltaY * WHEEL_ZOOM_SENSITIVITY);
      zoomBy(factor, localPoint(viewportEl, e.clientX, e.clientY));
    };
    viewportEl.addEventListener('wheel', handler, { passive: false });
    return () => viewportEl.removeEventListener('wheel', handler);
  }, [viewportEl, zoomBy]);

  // ── Keyboard ──────────────────────────────────────────────────────────────
  const onKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLElement>) => {
      if (cropRef.current.status !== 'ready') return;
      const step = e.shiftKey ? KEY_PAN_STEP_FAST : KEY_PAN_STEP;
      switch (e.key) {
        case 'ArrowLeft':
          panBy(-step, 0);
          break;
        case 'ArrowRight':
          panBy(step, 0);
          break;
        case 'ArrowUp':
          panBy(0, -step);
          break;
        case 'ArrowDown':
          panBy(0, step);
          break;
        case '+':
        case '=':
          zoomBy(KEY_ZOOM_FACTOR);
          break;
        case '-':
        case '_':
          zoomBy(1 / KEY_ZOOM_FACTOR);
          break;
        case 'Home':
          reset();
          break;
        default:
          return;
      }
      e.preventDefault();
    },
    [panBy, zoomBy, reset],
  );

  // ── Export ────────────────────────────────────────────────────────────────
  const exportSquare = useCallback(
    async (size = DEFAULT_EXPORT_PX, quality = DEFAULT_EXPORT_QUALITY): Promise<File> => {
      const c = cropRef.current;
      if (c.status !== 'ready' || !c.loaded) throw new Error('IMAGE_NOT_READY');
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('CANVAS_UNAVAILABLE');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, size, size);
      const { sx, sy, sw, sh } = sourceRect(c, viewportRef.current);
      ctx.drawImage(c.loaded.element, sx, sy, sw, sh, 0, 0, size, size);
      const blob = await new Promise<Blob | null>((resolve) => {
        if (typeof canvas.toBlob !== 'function') {
          resolve(null);
          return;
        }
        canvas.toBlob(resolve, 'image/jpeg', quality);
      });
      if (!blob) throw new Error('CANVAS_UNAVAILABLE');
      return new File([blob], 'avatar.jpg', { type: 'image/jpeg' });
    },
    [],
  );

  // ── Derived presentation ──────────────────────────────────────────────────
  const ariaLabel = options.ariaLabel ?? DEFAULT_ARIA_LABEL;
  const viewportProps: ViewportProps = useMemo(
    () => ({
      ref: setViewportEl,
      tabIndex: 0,
      role: 'group',
      'aria-label': ariaLabel,
      'aria-busy': crop.status === 'loading',
      onPointerDown,
      onPointerMove,
      onPointerUp: endPointer,
      onPointerCancel: endPointer,
      onKeyDown,
      style: { touchAction: 'none' },
    }),
    [ariaLabel, crop.status, onPointerDown, onPointerMove, endPointer, onKeyDown],
  );

  const transformStyle = useCallback(
    (k = 1): CSSProperties => ({
      position: 'absolute',
      top: 0,
      left: 0,
      width: crop.natural.w,
      height: crop.natural.h,
      maxWidth: 'none',
      transformOrigin: '0 0',
      transform: `translate(${crop.offset.x * k}px, ${crop.offset.y * k}px) scale(${crop.scale * k})`,
      willChange: 'transform',
    }),
    [crop.natural.w, crop.natural.h, crop.offset.x, crop.offset.y, crop.scale],
  );

  const imageStyle = useMemo(() => transformStyle(1), [transformStyle]);
  const previewStyle = useCallback(
    (size: number): CSSProperties => transformStyle(viewport > 0 ? size / viewport : 1),
    [transformStyle, viewport],
  );

  const zoomPercent =
    crop.status === 'ready' && crop.minScale > 0
      ? Math.round((crop.scale / crop.minScale) * 100)
      : 100;

  return {
    status: crop.status,
    error: crop.error,
    natural: crop.natural,
    scale: crop.scale,
    minScale: crop.minScale,
    maxScale: crop.maxScale,
    offset: crop.offset,
    zoomPercent,
    viewport,
    imageSrc: crop.loaded?.src ?? null,
    viewportProps,
    imageStyle,
    previewStyle,
    panBy,
    setScale,
    zoomBy,
    reset,
    exportSquare,
  };
}

export type ImageCrop = ReturnType<typeof useImageCrop>;
